import { Phone, Video, Calendar, Loader2 } from "lucide-react";

const STATUS_BADGE = {
  completed: { label: "Completada", cls: "bg-emerald-100 text-emerald-700" },
  scheduled: { label: "Programada", cls: "bg-blue-100 text-blue-700" },
  missed: { label: "No asistida", cls: "bg-red-100 text-red-700" },
};

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function SessionRow({ session, isAirtable }) {
  const badge = STATUS_BADGE[session.status] || {
    label: session.status,
    cls: "bg-gray-100 text-gray-600",
  };

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Phone size={14} className="text-gray-400 shrink-0" />
          <span className="text-gray-900">{session.type || "Sesión"}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-gray-600">{formatDate(session.date)}</td>
      <td className="px-4 py-3 text-gray-600">{session.time || "—"}</td>
      <td className="px-4 py-3">
        <span
          className={`px-2 py-0.5 text-xs font-medium rounded-full ${badge.cls}`}
        >
          {badge.label}
        </span>
      </td>
      <td className="px-4 py-3 text-gray-600">{session.mentor || "—"}</td>
      <td className="px-4 py-3 text-gray-500 text-xs max-w-[200px] truncate">
        {session.notes || "—"}
      </td>
      {isAirtable && session.meeting_url && (
        <td className="px-4 py-3 text-right">
          <a
            href={session.meeting_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-xs font-medium"
          >
            <Video size={13} />
            Unirse
          </a>
        </td>
      )}
      {isAirtable && !session.meeting_url && (
        <td className="px-4 py-3" />
      )}
    </tr>
  );
}

function SessionTable({ title, icon: Icon, sessions, isAirtable, emptyText }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
        <Icon size={16} className="text-gray-500" />
        <span className="text-sm font-semibold text-gray-900">{title}</span>
        <span className="text-xs text-gray-400 ml-auto">
          {sessions.length}
        </span>
      </div>
      {sessions.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">{emptyText}</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Tipo</th>
              <th className="text-left px-4 py-2 font-medium">Fecha</th>
              <th className="text-left px-4 py-2 font-medium">Hora</th>
              <th className="text-left px-4 py-2 font-medium">Estado</th>
              <th className="text-left px-4 py-2 font-medium">Mentor</th>
              <th className="text-left px-4 py-2 font-medium">Notas</th>
              {isAirtable && (
                <th className="text-right px-4 py-2 font-medium">Link</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sessions.map((s) => (
              <SessionRow key={s.id} session={s} isAirtable={isAirtable} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function SessionsTab({ sessions, isAirtable, loading }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-3">
        <Loader2 size={24} className="animate-spin text-indigo-500" />
        <span className="text-sm text-gray-500">Cargando sesiones...</span>
      </div>
    );
  }

  if (!sessions || sessions.length === 0) {
    return (
      <div className="text-center py-16">
        <Calendar size={40} className="mx-auto text-gray-300 mb-3" />
        <p className="text-gray-400">No hay sesiones registradas</p>
      </div>
    );
  }

  const upcoming = sessions.filter((s) => s.status === "scheduled");
  const completed = sessions.filter((s) => s.status !== "scheduled");

  return (
    <div className="space-y-4">
      {isAirtable && (
        <p className="text-xs text-gray-400">Datos cargados desde Airtable</p>
      )}

      <SessionTable
        title="Próximas"
        icon={Calendar}
        sessions={upcoming}
        isAirtable={isAirtable}
        emptyText="No hay sesiones programadas"
      />

      <SessionTable
        title="Completadas / Pasadas"
        icon={Phone}
        sessions={completed}
        isAirtable={isAirtable}
        emptyText="No hay sesiones completadas"
      />
    </div>
  );
}
